import { type JSX,useCallback, useEffect, useLayoutEffect, useState } from 'react';

import { Stack } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import { useDrawerContext } from 'common/components/DrawerContext/drawerContext';
import { EditDrawer } from 'common/components/EditDrawer';
import {
    selectCurrentOperation,
    setNameInConflict
} from 'common/components/EditDrawer/editDrawerSlice';
import { ErrorHandler } from 'common/components/errorHandler/ErrorHandler';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { ExpandableTextArea, ExpandableTextAreaProps } from 'common/components/sharedCustomComponents/ExpandableTextArea/ExpandableTextArea';
import { PalmDatePicker } from 'common/components/sharedCustomComponents/PalmDatePicker/PalmDatePicker';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { APILicensesPostParams, APILicensesPutParams } from 'common/services/api/Licenses/LicenseApiTypeDef';
import { fetchLicenses,postLicense, putLicense } from 'common/services/api/Licenses/Licenses.thunk';
import { PostLicenseThunkResponse, PutLicenseThunkResponse } from 'common/services/api/Licenses/LicenseThunkTypeDefs';
import { BillingSystems, License, LicenseFormatted } from 'common/types/Entities';
import { EditDrawerContentProps, Operations } from 'common/types/TypeDefs';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { DEFAULT_ERROR_MESSAGE } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { dateToFrontEndFormat, toESTDateString } from '../../common/utils/dateHelpers';

import { ExistingCibsCustomField } from './components/ExistingCibsCustomField';
import { LicenseAutoRenewalCheckbox } from './components/LicenseAutoRenewalCheckbox';
import { LicenseCustomTextField } from './components/LicenseCustomTextField';
import { useDateValidation } from './hooks/useDateValidation';
import { useInput } from './hooks/useInput';
import { useValidateRequiredLicenseFieldsOnCreate } from './hooks/useValidateRequiredLicenseFieldsOnCreate';
import { useValidateRequiredLicenseFieldsOnEdit } from './hooks/useValidateRequiredLicenseFieldsOnEdit';
import { IPayload, LicenseDrawerProperties, LicenseItem } from './ILicenseCreateDrawerContent';
import { _cibsAlreadyExists,_convertDateToValidFormat, _duplicatedCibsNotConfirmed, _isEditFormInfoExists, _reformatDateString } from './LicenseCreateEditHelpers';
import {
    addNewFilter,
    clearFilters
} from './licensesSlice';

const LICENSE_NOTE_TEXT_AREA_PLACEHOLDER = 'Add a note here';
const LICENSE_NOTE_TEXT_AREA_LABEL = 'License Note';
const LICENSE_NOTE_TEXT_AREA_POPOUT_LABEL = 'License Notes (optional)';
const LICENSE_NOTE_TEXT_AREA_POPOUT_PLACEHOLDER = 'Add a note here';
const LICENSE_CREATE_ERROR = 'There was an error while creating the License.';
const LICENSE_EDIT_ERROR = 'There was an error while editing the License.';

function DuplicateCibsField({
    cibsAlreadyExists,
    billingRecord,
    showDuplicatedCibs,
    setSaveDuplicates,
    saveDuplicates
}: Readonly<{
    cibsAlreadyExists: boolean;
    billingRecord: string;
    showDuplicatedCibs: (billingRecord: string) => void;
    setSaveDuplicates: (checked: boolean) => void;
    saveDuplicates: boolean;
}>) {
    if (!cibsAlreadyExists) return null;
    return (
        <ExistingCibsCustomField
            billingRecord={billingRecord}
            showDuplicates={() => showDuplicatedCibs(billingRecord)}
            saveDuplicatesOnChange={setSaveDuplicates}
            saveDuplicatesChecked={saveDuplicates}
            testId={'duplicate-cibs'}
        />
    );
}

function useLicenseDrawer(
    entityName: string,
    drawerProps: LicenseDrawerProperties,
    editingItemInfo?: EditDrawerContentProps<License & LicenseItem>['editingItemInfo']
) {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [licenseNotes, setLicenseNotes] = useState<string>('');
    const [billingSystemValue, setBillingSystemValue] = useState<BillingSystems | null>(null);
    const [licenseAutoRenewalIsChecked, setLicenseAutoRenewalIsChecked] = useState<boolean>(true);
    const [endDateEditPreviousValue, setEndDateEditPreviousValue] = useState<string>('');
    const [cibsAlreadyExists, setCibsAlreadyExists] = useState<boolean>(false);
    const [licenses, setLicenses] = useState<LicenseFormatted[]>([]);
    const [saveDuplicates, setSaveDuplicates] = useState<boolean>(false);

    const drawerContext = useDrawerContext<
        License & LicenseItem,
        LicenseDrawerProperties,
        APILicensesPostParams,
        PostLicenseThunkResponse,
        APILicensesPutParams,
        PutLicenseThunkResponse
    >(entityName, drawerProps, editingItemInfo);

    const currentOperation = useAppSelector(selectCurrentOperation);
    const datesValidation = useDateValidation(drawerProps.startDateField.value as string, drawerProps.endDateField.value as string);
    const editDatesValidation = useDateValidation(drawerProps.startDateField.value as string, drawerProps.endDateEditField.value as string);

    useValidateRequiredLicenseFieldsOnCreate(
        {
            companyKey: drawerProps.companyKeyField.value as string || '',
            startDate: _convertDateToValidFormat(drawerProps.startDateField.value as string),
            billingSystemId: billingSystemValue?.id ?? import.meta.env.VITE_CIBS_ID,
            billingRecord: drawerProps.billingRecordField.value as string || ''
        },
        {
            companyKey: drawerProps.companyKeyField.value as string || '',
            startDate: _convertDateToValidFormat(drawerProps.startDateField.value as string),
            endDate: _convertDateToValidFormat(drawerProps.endDateField.value as string),
            billingSystemId: billingSystemValue?.id ?? '',
            billingRecord: drawerProps.billingRecordField.value as string || ''
        },
        datesValidation.isError,
        _duplicatedCibsNotConfirmed(cibsAlreadyExists, saveDuplicates),
        currentOperation,
        licenseAutoRenewalIsChecked
    );

    useValidateRequiredLicenseFieldsOnEdit(
        {
            startDate: _convertDateToValidFormat(drawerProps.startDateField.value as string),
            billingSystemId: billingSystemValue?.id ?? '',
            billingRecord: drawerProps.billingRecordField.value as string || ''
        },
        {
            startDate: _convertDateToValidFormat(drawerProps.startDateField.value as string),
            endDate: _convertDateToValidFormat(drawerProps.endDateEditField.value as string),
            billingSystemId: billingSystemValue?.id ?? '',
            billingRecord: drawerProps.billingRecordField.value as string || ''
        },
        editDatesValidation.isError,
        _duplicatedCibsNotConfirmed(cibsAlreadyExists, saveDuplicates),
        currentOperation,
        licenseAutoRenewalIsChecked
    );

    useEffect(() => {
        dispatch(fetchLicenses({ tenantId: drawerProps.tenantId })).unwrap()
            .then(response => {
                setLicenses(response);
            });
    }, []);

    // Handle auto renewal toggle changes for Edit form
    useEffect(() => {
        if (drawerContext.currentId === DrawerIDs.License && drawerContext.action === Operations.Edit && drawerContext.isOpenDrawer) {
            if (licenseAutoRenewalIsChecked && drawerProps.endDateEditField.value) {
                setEndDateEditPreviousValue(drawerProps.endDateEditField.value as string);
                drawerProps.endDateEditField.changeHandler('');
            } else if (!licenseAutoRenewalIsChecked && !drawerProps.endDateEditField.value && endDateEditPreviousValue) {
                const endDateCurrentValue = _reformatDateString(endDateEditPreviousValue);
                drawerProps.endDateEditField.changeHandler(endDateCurrentValue);
            }
        } else {
            setEndDateEditPreviousValue('');
        }
    }, [licenseAutoRenewalIsChecked, drawerContext.isOpenDrawer]);

    useEffect(() => {
        if (drawerContext.currentId === DrawerIDs.License && drawerContext.action === Operations.Create && licenseAutoRenewalIsChecked) {
            drawerProps.endDateField.changeHandler('');
        }
    }, [drawerContext.isOpenDrawer, licenseAutoRenewalIsChecked]);

    useLayoutEffect(() => {
        if (drawerContext.currentId === DrawerIDs.License) {
            if (drawerContext.action === Operations.Create) {
                setCibsAlreadyExists(false);
                const formattedTodayDate = _reformatDateString(dateToFrontEndFormat(toESTDateString(new Date())));
                const cibsBillingSystemId = import.meta.env.VITE_CIBS_ID;

                drawerProps.companyKeyField.changeHandler('');
                drawerProps.billingRecordField.changeHandler('');
                drawerProps.startDateField.changeHandler(formattedTodayDate);
                drawerProps.endDateField.changeHandler('');
                setLicenseNotes('');
                setBillingSystemValue({ id: cibsBillingSystemId, name: 'CIBS' });
                setLicenseAutoRenewalIsChecked(true);
                return;
            } else if (drawerContext.action === Operations.Edit) {
                const cibsExists = _cibsAlreadyExists(licenses, editingItemInfo?.editingRow?.billingRecord ?? '', editingItemInfo?.editingRow?.id);
                setCibsAlreadyExists(cibsExists);
                setSaveDuplicates(cibsExists);
            }
        }
    }, [drawerContext.isOpenDrawer, drawerContext.currentId, drawerContext.action]);

    useEffect(() => {
        if (_isEditFormInfoExists(drawerContext.action, editingItemInfo)) {
            const licenseNotesCurrentValue = editingItemInfo?.editingRow?.note ?? '';
            const startDateCurrentValue = _reformatDateString(editingItemInfo?.editingRow?.startDate ?? '');
            const endDateCurrentValue = _reformatDateString(editingItemInfo?.editingRow?.endDate ?? '');
            setEndDateEditPreviousValue('');
            const billingRecordCurrentValue = editingItemInfo?.editingRow?.billingRecord ?? '';
            const billingSystem = {
                id: editingItemInfo?.editingRow?.billingSystemId ?? '',
                name: editingItemInfo?.editingRow?.billingSystemName ?? ''
            };
            const licenseAutoRenewalCurrentValue = editingItemInfo?.editingRow?.licenseAutoRenewal ?? false;

            setLicenseNotes(licenseNotesCurrentValue);
            drawerProps.startDateField.changeHandler(startDateCurrentValue);
            drawerProps.endDateEditField.changeHandler(endDateCurrentValue);
            drawerProps.billingRecordField.changeHandler(billingRecordCurrentValue);
            setBillingSystemValue(billingSystem);
            setLicenseAutoRenewalIsChecked(licenseAutoRenewalCurrentValue);
        }
    }, [
        drawerContext.isOpenDrawer,
        editingItemInfo?.editingRow?.note,
        editingItemInfo?.editingRow?.startDate,
        editingItemInfo?.editingRow?.endDate,
        editingItemInfo?.editingRow?.billingRecord,
        editingItemInfo?.editingRow?.licenseAutoRenewal
    ]);

    const createSuccessHandler = useCallback((postResult: PostLicenseThunkResponse) => {
        drawerContext.createSuccessHandler(postResult, false);
        dispatch(setRows(postResult.rows));
        const id = postResult.response.data.licenseId;
        drawerContext.navigate(id);
    }, [drawerContext, dispatch]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const createFailureHandler = useCallback((error: any) => {
        dispatch(setNameInConflict(false));
        if (extractStatusCode(error.message) === 409) {
            dispatch(setNameInConflict(true));
        } else {
            enqueueSnackbar(
                LICENSE_CREATE_ERROR +
                DEFAULT_ERROR_MESSAGE,
                { variant: 'error' }
            );
        }
    }, [dispatch, enqueueSnackbar]);

    const editSuccessHandler = useCallback((putResult: PutLicenseThunkResponse) => {
        drawerContext.editSuccessHandler(putResult);
        dispatch(setRows(putResult.rows));
    }, [drawerContext, dispatch]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const editFailureHandler = useCallback((error: any) => {
        dispatch(setNameInConflict(false));
        if (extractStatusCode(error.message) === 409) {
            dispatch(setNameInConflict(true));
        } else {
            enqueueSnackbar(
                LICENSE_EDIT_ERROR +
                DEFAULT_ERROR_MESSAGE,
                { variant: 'error' }
            );
        }
    }, [dispatch, enqueueSnackbar]);

    const getPostOptions = useCallback((): APILicensesPostParams => ({
        tenantId: drawerProps.tenantId,
        companyKey: drawerProps.companyKeyField.value as string || '',
        startDate: _convertDateToValidFormat(drawerProps.startDateField.value as string),
        endDate: _convertDateToValidFormat(drawerProps.endDateField.value as string) || null,
        note: licenseNotes,
        billingSystemId: billingSystemValue?.id ?? '',
        billingRecord: drawerProps.billingRecordField.value as string || ''
    }), [drawerProps, licenseNotes, billingSystemValue?.id]);

    const getPutOptions = useCallback((): IPayload => ({
        tenantId: drawerProps.tenantId,
        startDate: _convertDateToValidFormat(drawerProps.startDateField.value as string),
        endDate: _convertDateToValidFormat(drawerProps.endDateEditField.value as string) || null,
        note: licenseNotes,
        licenseId: editingItemInfo?.editingRow?.id ?? '',
        billingSystemId: billingSystemValue?.id ?? '',
        billingRecord: drawerProps.billingRecordField.value as string || ''
    }), [drawerProps, licenseNotes, editingItemInfo?.editingRow?.id, billingSystemValue?.id]);

    const createLicenseNotesTextAreaContext = useCallback((): ExpandableTextAreaProps => ({
        textAreaCommonProps: {
            value: licenseNotes,
            onChange: setLicenseNotes,
            placeholder: LICENSE_NOTE_TEXT_AREA_PLACEHOLDER,
            label: LICENSE_NOTE_TEXT_AREA_LABEL
        },
        popoutCommonProps: {
            popoutInputBaseProps: {
                inputBasePlaceholder: LICENSE_NOTE_TEXT_AREA_POPOUT_PLACEHOLDER
            },
            popoutInputLabel: LICENSE_NOTE_TEXT_AREA_POPOUT_LABEL
        }
    }), [licenseNotes]);

    const updateCIBS = useCallback((newValue: unknown) => {
        drawerProps.billingRecordField.changeHandler(newValue);
        setCibsAlreadyExists(_cibsAlreadyExists(licenses, newValue as string, editingItemInfo?.editingRow?.id));
        setSaveDuplicates(false);
    }, [drawerProps.billingRecordField, licenses, editingItemInfo?.editingRow?.id]);

    const showDuplicatedCibs = useCallback((billingRecord: string) => {
        dispatch(clearFilters());
        dispatch(addNewFilter(billingRecord));
    }, [dispatch]);

    const postCallback = useCallback(() => {
        drawerContext.postCallback(postLicense, getPostOptions, createSuccessHandler, createFailureHandler);
    }, [drawerContext, getPostOptions, createSuccessHandler, createFailureHandler]);

    const putCallback = useCallback(() => {
        drawerContext.putCallback(putLicense, getPutOptions, editSuccessHandler, editFailureHandler);
    }, [drawerContext, getPutOptions, editSuccessHandler, editFailureHandler]);

    return {
        ...drawerContext,
        licenseNotes,
        licenseAutoRenewalIsChecked,
        cibsAlreadyExists,
        saveDuplicates,
        datesValidation,
        editDatesValidation,
        setLicenseAutoRenewalIsChecked,
        setSaveDuplicates,
        createLicenseNotesTextAreaContext,
        updateCIBS,
        showDuplicatedCibs,
        postCallback,
        putCallback
    };
}

function CreateContent({
    entityName,
    processing,
    drawerProps,
    licenseAutoRenewalIsChecked,
    cibsAlreadyExists,
    saveDuplicates,
    datesValidation,
    setLicenseAutoRenewalIsChecked,
    setSaveDuplicates,
    createLicenseNotesTextAreaContext,
    updateCIBS,
    showDuplicatedCibs,
    postCallback
}: Readonly<{
    entityName: string;
    processing: boolean;
    drawerProps: LicenseDrawerProperties;
    licenseAutoRenewalIsChecked: boolean;
    cibsAlreadyExists: boolean;
    saveDuplicates: boolean;
    datesValidation: { isError: boolean; errorMessage: string };
    setLicenseAutoRenewalIsChecked: (checked: boolean) => void;
    setSaveDuplicates: (checked: boolean) => void;
    createLicenseNotesTextAreaContext: () => ExpandableTextAreaProps;
    updateCIBS: (newValue: unknown) => void;
    showDuplicatedCibs: (billingRecord: string) => void;
    postCallback: () => void;
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={postCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <LicenseCustomTextField
                    label='Company Key'
                    placeholder='Type Company Key'
                    value={drawerProps.companyKeyField.value as string}
                    onChange={drawerProps.companyKeyField.changeHandler}
                />
                <LicenseCustomTextField
                    label='CIBS Billing Record'
                    placeholder='Type Billing Record'
                    value={drawerProps.billingRecordField.value as string}
                    onChange={updateCIBS}
                />
                <DuplicateCibsField
                    cibsAlreadyExists={cibsAlreadyExists}
                    billingRecord={drawerProps.billingRecordField.value as string}
                    showDuplicatedCibs={showDuplicatedCibs}
                    setSaveDuplicates={setSaveDuplicates}
                    saveDuplicates={saveDuplicates}
                />
                <LicenseAutoRenewalCheckbox
                    checked={licenseAutoRenewalIsChecked}
                    changeHandler={setLicenseAutoRenewalIsChecked}
                />
                <Stack
                    display="flex"
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <PalmDatePicker
                            label='Start Date'
                            onChange={drawerProps.startDateField.changeHandler}
                            required
                            value={drawerProps.startDateField.value as string}
                            testId='start-datepicker-test-id'
                        ></PalmDatePicker>
                        <PalmDatePicker
                            label='End Date'
                            onChange={drawerProps.endDateField.changeHandler}
                            value={drawerProps.endDateField.value as string}
                            testId='end-datepicker-test-id'
                            required={!licenseAutoRenewalIsChecked}
                            disabled={licenseAutoRenewalIsChecked}
                        ></PalmDatePicker>
                    </LocalizationProvider>
                </Stack>
                {datesValidation.isError &&
                    <ErrorHandler
                        component='div'
                        display='inline'
                        errorMessage={datesValidation.errorMessage}
                    />
                }
                <ExpandableTextArea {...createLicenseNotesTextAreaContext()} />
                <LicenseCustomTextField
                    label='Tenant Name'
                    defaultValue={drawerProps.tenantName}
                    disabled={true}
                />
            </Stack>
        </EditDrawer>
    );
}

function EditContent({
    entityName,
    processing,
    drawerProps,
    editingItemInfo,
    licenseAutoRenewalIsChecked,
    cibsAlreadyExists,
    saveDuplicates,
    editDatesValidation,
    setLicenseAutoRenewalIsChecked,
    setSaveDuplicates,
    createLicenseNotesTextAreaContext,
    updateCIBS,
    showDuplicatedCibs,
    putCallback
}: Readonly<{
    entityName: string;
    processing: boolean;
    drawerProps: LicenseDrawerProperties;
    editingItemInfo?: EditDrawerContentProps<License & LicenseItem>['editingItemInfo'];
    licenseAutoRenewalIsChecked: boolean;
    cibsAlreadyExists: boolean;
    saveDuplicates: boolean;
    editDatesValidation: { isError: boolean; errorMessage: string };
    setLicenseAutoRenewalIsChecked: (checked: boolean) => void;
    setSaveDuplicates: (checked: boolean) => void;
    createLicenseNotesTextAreaContext: () => ExpandableTextAreaProps;
    updateCIBS: (newValue: unknown) => void;
    showDuplicatedCibs: (billingRecord: string) => void;
    putCallback: () => void;
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={putCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <LicenseCustomTextField
                    label='Company Key'
                    defaultValue={editingItemInfo?.editingRow?.companyKey}
                    disabled={true}
                />
                <LicenseCustomTextField
                    label='CIBS Billing Record'
                    placeholder='Type Billing Record'
                    value={drawerProps.billingRecordField.value as string}
                    onChange={updateCIBS}
                />
                <DuplicateCibsField
                    cibsAlreadyExists={cibsAlreadyExists}
                    billingRecord={drawerProps.billingRecordField.value as string}
                    showDuplicatedCibs={showDuplicatedCibs}
                    setSaveDuplicates={setSaveDuplicates}
                    saveDuplicates={saveDuplicates}
                />
                <LicenseAutoRenewalCheckbox
                    testId='edit-auto-renewal'
                    checked={licenseAutoRenewalIsChecked}
                    changeHandler={setLicenseAutoRenewalIsChecked}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack
                        display="flex"
                        direction="row"
                        justifyContent="space-between"
                        spacing={2}>
                        <PalmDatePicker
                            label='Start Date'
                            onChange={drawerProps.startDateField.changeHandler}
                            required
                            value={drawerProps.startDateField.value as string}
                            testId='start-datepicker-test-id'
                        ></PalmDatePicker>
                        <PalmDatePicker
                            label='End Date'
                            onChange={drawerProps.endDateEditField.changeHandler}
                            value={drawerProps.endDateEditField.value as string}
                            testId='end-datepicker-test-id'
                            disabled={licenseAutoRenewalIsChecked}
                        ></PalmDatePicker>
                    </Stack>
                </LocalizationProvider>
                {editDatesValidation.isError &&
                    <ErrorHandler
                        component='div'
                        display='inline'
                        errorMessage={editDatesValidation.errorMessage}
                    />
                }
                <ExpandableTextArea {...createLicenseNotesTextAreaContext()} />
                <LicenseCustomTextField
                    label='Tenant Name'
                    defaultValue={drawerProps.tenantName}
                    disabled={true}
                />
            </Stack>
        </EditDrawer>
    );
}

export function LicenseCreateContent({
    title,
    editingItemInfo,
}: Readonly<EditDrawerContentProps<License & LicenseItem>>) {
    const pageInfo = useAppSelector(selectPageInfo);
    const licenseDrawerProps: LicenseDrawerProperties = {
        tenantId: pageInfo?.id ?? '',
        tenantName: pageInfo?.name ?? '',
        companyKeyField: useInput(''),
        billingRecordField: useInput(''),
        startDateField: useInput(''),
        endDateField: useInput(''),
        endDateEditField: useInput(''),
    };

    const drawerState = useLicenseDrawer(title, licenseDrawerProps, editingItemInfo);

    if (drawerState.action === Operations.Create || drawerState.action === Operations.Link) {
        return (
            <CreateContent
                entityName={title}
                processing={drawerState.processing}
                drawerProps={licenseDrawerProps}
                licenseAutoRenewalIsChecked={drawerState.licenseAutoRenewalIsChecked}
                cibsAlreadyExists={drawerState.cibsAlreadyExists}
                saveDuplicates={drawerState.saveDuplicates}
                datesValidation={drawerState.datesValidation}
                setLicenseAutoRenewalIsChecked={drawerState.setLicenseAutoRenewalIsChecked}
                setSaveDuplicates={drawerState.setSaveDuplicates}
                createLicenseNotesTextAreaContext={drawerState.createLicenseNotesTextAreaContext}
                updateCIBS={drawerState.updateCIBS}
                showDuplicatedCibs={drawerState.showDuplicatedCibs}
                postCallback={drawerState.postCallback}
            />
        );
    } else if (drawerState.action === Operations.Edit) {
        if (!editingItemInfo) {
            return <></>;
        }
        return (
            <EditContent
                entityName={title}
                processing={drawerState.processing}
                drawerProps={licenseDrawerProps}
                editingItemInfo={editingItemInfo}
                licenseAutoRenewalIsChecked={drawerState.licenseAutoRenewalIsChecked}
                cibsAlreadyExists={drawerState.cibsAlreadyExists}
                saveDuplicates={drawerState.saveDuplicates}
                editDatesValidation={drawerState.editDatesValidation}
                setLicenseAutoRenewalIsChecked={drawerState.setLicenseAutoRenewalIsChecked}
                setSaveDuplicates={drawerState.setSaveDuplicates}
                createLicenseNotesTextAreaContext={drawerState.createLicenseNotesTextAreaContext}
                updateCIBS={drawerState.updateCIBS}
                showDuplicatedCibs={drawerState.showDuplicatedCibs}
                putCallback={drawerState.putCallback}
            />
        );
    }

    return <></>;
}
